// AdminHome — queue-focused landing for staff (admin + manager). Replaces
// the reseller-oriented MyStore dashboard when the user is staff.
//
// Goals:
//   • show every queue that's awaiting human action, with counts
//   • one-click into the right AdminPanel tab for each queue
//   • surface the most recent items so "what's new" is glanceable
//     without diving in
//
// We deliberately keep this lightweight — it pulls only the endpoints
// that already exist (application counts + curated open list). Deeper
// status comes from AdminPanel itself.

import { useState, useEffect, useMemo } from "react";
import { plural } from "../i18n.jsx";
import { api } from "../api.js";
import { T_NEW } from "../theme.js";
import { Btn, Mono, Serif } from "./ui.jsx";
import { useToast } from "./toast.jsx";
import { useT } from "../i18n.jsx";

export function AdminHome({ user, onOpenAdminTab }) {
  const toast = useToast();
  const t = useT();
  const [appCounts, setAppCounts] = useState({ submitted: 0, pending: 0, approved: 0, rejected: 0 });
  const [curated, setCurated] = useState([]);
  const [recentApps, setRecentApps] = useState([]);
  const [loading, setLoading] = useState(true);
  // Viewport-width gate. We can't rely on CSS-only `minmax(min(…), 1fr)`
  // because WebKit's auto-fit + min() combo silently picks 3 columns
  // of 240px on iPhone and forces the whole panel wider than the
  // viewport. JS detection is bulletproof.
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 600,
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [counts, openCurated, submittedApps] = await Promise.all([
          api.adminApplicationCounts().catch(() => null),
          api.adminListCurated().catch(() => []),
          api.adminListApplications("submitted").catch(() => []),
        ]);
        if (counts) setAppCounts(counts);
        setCurated(openCurated || []);
        setRecentApps((submittedApps || []).slice(0, 5));
      } catch (e) { toast.error(e.message); }
      setLoading(false);
    })();
  }, []);

  // Curated bucket counts — derived from the open-list (single fetch)
  // so we don't need a separate counts endpoint. Same UX as the
  // applications block where counts come pre-computed.
  const curatedBuckets = useMemo(() => {
    const buckets = { submitted: 0, priced: 0, purchased: 0, invoiced: 0, paid: 0 };
    for (const r of curated) if (r.status in buckets) buckets[r.status]++;
    return buckets;
  }, [curated]);
  const curatedActionable = curatedBuckets.submitted + curatedBuckets.priced
    + curatedBuckets.purchased + curatedBuckets.invoiced + curatedBuckets.paid;

  return (
    <div style={{ padding: "20px 4px 40px",
                  // Hard-cap to the parent's width. minWidth:0 lets us
                  // shrink below any child's intrinsic min-content
                  // (Safari's main culprit on iPhone). overflowX:hidden
                  // is the final seatbelt — if any child still tries
                  // to drag wider, we clip it instead of dragging the
                  // whole panel past the viewport edge.
                  width: "100%",
                  minWidth: 0,
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  overflowX: "hidden" }}>
      <div style={{ marginBottom: 24 }}>
        <Mono color={T_NEW.persimmon} size={T_NEW.micro}
              style={{ display: "block", fontWeight: 700, letterSpacing: 0.6,
                       textTransform: "uppercase", marginBottom: 6 }}>
          {t("admin.home.kicker")}
        </Mono>
        {/* Fluid heading — clamp shrinks the greeting on narrow
            viewports so the iPhone doesn't get a clipped one-liner.
            overflowWrap:anywhere is the seatbelt for unusually long
            display names. */}
        <Serif color={T_NEW.ink}
               style={{ fontWeight: 500, lineHeight: 1.1,
                        fontSize: "clamp(22px, 5vw, 36px)",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        display: "block" }}>
          {hourGreeting(t)}, {user.displayName?.split(" ")[0] || "Admin"}.
        </Serif>
        <div style={{ fontSize: 13, color: T_NEW.ink56, marginTop: 6,
                      fontFamily: T_NEW.fontSerif, fontStyle: "italic",
                      overflowWrap: "anywhere" }}>
          {t("admin.home.subtitle")}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: T_NEW.ink56 }}>
          {t("admin.home.loading")}
        </div>
      ) : (
        <>
          {/* Top row: actionable queues. Capped at 3-up max so cards
              read as tiles on desktop, stack on mobile. Each card
              shows the count of items that need a human decision
              right now. */}
          <div style={{ display: "grid",
                        gridTemplateColumns: isNarrow
                          ? "1fr"
                          : "repeat(auto-fit, minmax(240px, 1fr))",
                        gap: 12, marginBottom: 24 }}>
            <QueueCard
              label={t("admin.home.card.applications.label")}
              count={appCounts.submitted}
              hint={appCounts.submitted === 0
                ? t("admin.home.card.applications.empty")
                : t("admin.home.card.applications.hint")}
              tone="primary"
              onClick={() => onOpenAdminTab("applications")}
            />
            <QueueCard
              label={t("admin.home.card.curated.label")}
              count={curatedActionable}
              hint={curatedActionable === 0
                ? t("admin.home.card.curated.empty")
                : t("admin.home.card.curated.hint")
                    .replace("{n0}", curatedBuckets.submitted)
                    .replace("{n1}", curatedBuckets.invoiced)}
              tone="primary"
              onClick={() => onOpenAdminTab("curated")}
            />
            <QueueCard
              label={t("admin.home.card.subscriptions.label")}
              count="—"
              hint={t("admin.home.card.subscriptions.hint")}
              tone="muted"
              onClick={() => onOpenAdminTab("subscriptions")}
            />
          </div>

          {/* Curated-buys bucket strip — denser breakdown by status so
              admin can see where work is bottlenecking. */}
          {curatedActionable > 0 && (
            <div style={{ display: "grid",
                          gridTemplateColumns: isNarrow
                            ? "repeat(2, 1fr)"
                            : "repeat(5, 1fr)",
                          gap: 6, marginBottom: 24 }}>
              {[
                ["submitted",  t("admin.home.bucket.submitted")],
                ["priced",     t("admin.home.bucket.priced")],
                ["purchased",  t("admin.home.bucket.purchased")],
                ["invoiced",   t("admin.home.bucket.invoiced")],
                ["paid",       t("admin.home.bucket.paid")],
              ].map(([k, label]) => (
                <button key={k} type="button"
                        onClick={() => onOpenAdminTab("curated")}
                        style={{ padding: "10px 12px",
                                 background: T_NEW.paperSoft,
                                 border: `1px solid ${T_NEW.ink12}`,
                                 borderRadius: T_NEW.r1,
                                 cursor: "pointer", textAlign: "left",
                                 fontFamily: T_NEW.fontSans }}>
                  <div style={{ fontFamily: T_NEW.fontMono, fontSize: 18,
                                fontWeight: 700, color: T_NEW.ink }}>
                    {curatedBuckets[k]}
                  </div>
                  <div style={{ fontSize: 11, color: T_NEW.ink56,
                                textTransform: "uppercase", letterSpacing: 0.4,
                                marginTop: 2 }}>
                    {label}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Most recent applications — quick triage without leaving home */}
          {recentApps.length > 0 && (
            <Section title={t("admin.home.recent.apps")}
                     action={() => onOpenAdminTab("applications")}
                     actionLabel={t("admin.home.recent.openAll")}>
              {recentApps.map((a) => (
                <RowLink key={a.uid}
                         onClick={() => onOpenAdminTab("applications")}>
                  <div>
                    <Mono size={13} style={{ fontWeight: 700 }}>
                      {a.displayName || a.email}
                    </Mono>
                    <div style={{ fontSize: 12, color: T_NEW.ink56 }}>
                      {a.email}{a.nickname ? ` · @${a.nickname}` : ""}
                      {a.submittedAt
                        ? ` · ${timeAgo(a.submittedAt, t)}`
                        : ""}
                    </div>
                  </div>
                  {a.proof?.channelUrl && (
                    <a href={a.proof.channelUrl}
                       target="_blank" rel="noopener noreferrer"
                       onClick={(e) => e.stopPropagation()}
                       style={{ fontSize: 12, color: T_NEW.accent,
                                whiteSpace: "nowrap", marginLeft: 12 }}>
                      {t("admin.home.channelLink")}
                    </a>
                  )}
                </RowLink>
              ))}
            </Section>
          )}

          {/* Most recent curated requests */}
          {curated.length > 0 && (
            <Section title={t("admin.home.recent.curated")}
                     action={() => onOpenAdminTab("curated")}
                     actionLabel={t("admin.home.recent.openAll")}>
              {curated.slice(0, 6).map((r) => (
                <RowLink key={r.id}
                         onClick={() => onOpenAdminTab("curated")}>
                  <div>
                    <Mono size={13} style={{ fontWeight: 700 }}>
                      {r.resellerNickname ? `@${r.resellerNickname}` : r.resellerEmail}
                    </Mono>
                    <div style={{ fontSize: 12, color: T_NEW.ink56 }}>
                      {r.itemCount} {t(plural(r.itemCount, "admin.home.items"))} · ${(r.statedTotalCents / 100).toFixed(2)} · {r.preferredCurrency}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700,
                                 color: T_NEW.ink56,
                                 textTransform: "uppercase",
                                 letterSpacing: 0.5,
                                 marginLeft: 12, whiteSpace: "nowrap" }}>
                    {r.status}
                  </span>
                </RowLink>
              ))}
            </Section>
          )}

          {/* No "Система" duplicate-of-tabs grid — every button there
              already exists in the top tab strip and rendering them
              twice was just noise. Daily work happens through the
              queue cards above; one-off admin tasks reach for the
              tabs. */}
        </>
      )}
    </div>
  );
}

// ─── Tiny helpers ─────────────────────────────────────────────────────
function QueueCard({ label, count, hint, tone, onClick }) {
  const isPrimary = tone === "primary" && typeof count === "number" && count > 0;
  return (
    <button type="button" onClick={onClick}
            style={{ padding: 14, textAlign: "left", cursor: "pointer",
                     background: isPrimary ? `${T_NEW.persimmon}10` : T_NEW.paperRaised,
                     border: `1px solid ${isPrimary ? T_NEW.persimmon + "55" : T_NEW.ink12}`,
                     borderRadius: T_NEW.r2,
                     fontFamily: T_NEW.fontSans,
                     // Critical for grid layout: by default buttons have
                     // min-width:auto which equals their min-content
                     // (the longest unwrappable label). On iPhone that
                     // can force the grid cell wider than the viewport.
                     minWidth: 0,
                     width: "100%",
                     boxSizing: "border-box" }}>
      <Mono size={11} color={T_NEW.ink56}
            style={{ display: "block", letterSpacing: 0.5,
                     textTransform: "uppercase", marginBottom: 8 }}>
        {label}
      </Mono>
      <Serif size={32} color={T_NEW.ink}
             style={{ display: "block", fontWeight: 500, lineHeight: 1 }}>
        {count}
      </Serif>
      <div style={{ fontSize: 12, color: T_NEW.ink56, marginTop: 6,
                    fontFamily: T_NEW.fontSerif, fontStyle: "italic" }}>
        {hint}
      </div>
    </button>
  );
}

function Section({ title, action, actionLabel, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "baseline", marginBottom: 10 }}>
        <Serif size={18} color={T_NEW.ink} style={{ fontWeight: 600 }}>
          {title}
        </Serif>
        {action && actionLabel && (
          <button type="button" onClick={action}
                  style={{ background: "none", border: 0,
                           color: T_NEW.accent, cursor: "pointer",
                           fontSize: 12, padding: 0 }}>
            {actionLabel}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function RowLink({ children, onClick }) {
  return (
    <button type="button" onClick={onClick}
            style={{ display: "flex", width: "100%",
                     justifyContent: "space-between", alignItems: "center",
                     padding: 12, marginBottom: 6, textAlign: "left",
                     background: T_NEW.paperRaised,
                     border: `1px solid ${T_NEW.ink12}`,
                     borderRadius: T_NEW.r2, cursor: "pointer",
                     fontFamily: T_NEW.fontSans }}>
      {children}
    </button>
  );
}

function hourGreeting(t) {
  const h = new Date().getHours();
  if (h < 6)  return t("admin.home.greeting.night");
  if (h < 12) return t("admin.home.greeting.morning");
  if (h < 18) return t("admin.home.greeting.day");
  return t("admin.home.greeting.evening");
}

function timeAgo(ts, t) {
  const sec = Math.max(0, Math.floor(Date.now() / 1000) - ts);
  if (sec < 60)    return t("admin.home.timeAgo.now");
  if (sec < 3600)  return t("admin.home.timeAgo.min").replace("{n}", Math.floor(sec / 60));
  if (sec < 86400) return t("admin.home.timeAgo.hour").replace("{n}", Math.floor(sec / 3600));
  return t("admin.home.timeAgo.day").replace("{n}", Math.floor(sec / 86400));
}
